// ./src/components/features/EventGuests/GuestImportStatus.tsx
//
// ROADMAP #132 — where an event's background guest imports are (bulk jobs of kind `guest_import`): a progress card while one is still adding guests, and,
// once it finishes, its report (how many were added, matched, joined a family's invitation — and which rows were refused,
// with why) until the host closes it. Shown on every screen an import can be started from (the message composer, the
// wizard's guest step) and on the Guests page, so a host who left mid-import finds out how it went.
//
// ⚠ Reads nothing itself: the screen owns `useBulkJobs(BULK_JOB_KIND.GUEST_IMPORT, …)` (it also needs `track` and
//   `onFinished`) and passes it in. The job is generic; what its outcomes, payload and refusal codes MEAN is this file's.
import { useState } from "react";
import { ActionIcon, Box, Button, Group, Loader, Paper, Progress, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import { UseBulkJobsReturn } from "@/hooks/useBulkJobs";
import {
  BULK_JOB_REASON,
  BULK_JOB_STATE,
  BulkJob,
  BulkJobService,
  BulkJobSkippedRow,
  bulkJobCount,
  bulkJobIsOpen,
} from "@/utils/api/BulkJobService";
import { GUEST_IMPORT_ROW_OUTCOME, GUEST_IMPORT_SKIP_REASON, ImportGuestRecipient } from "@/utils/api/EventGuestService";
import { formatPhone } from "@em/utils";
import { refusalReason } from "@/utils";

/** The host's words for each refusal code. ⚠ Each says what is true and what to do — never "error". */
const SKIP_REASON_TEXT: Record<string, { key: string; text: string }> = {
  [GUEST_IMPORT_SKIP_REASON.NO_CONTACT]: { key: "events:guestImport.skip.noContact", text: "No valid email address or phone number." },
  [GUEST_IMPORT_SKIP_REASON.NOT_A_CONTACT]: {
    key: "events:guestImport.skip.notAContact",
    text: "This contact was removed, or is not one of your contacts.",
  },
  [GUEST_IMPORT_SKIP_REASON.NOT_SAVED]: { key: "events:guestImport.skip.notSaved", text: "The contact could not be saved. Add them again on their own." },
  [GUEST_IMPORT_SKIP_REASON.PLAN_LIMIT]: {
    key: "events:guestImport.skip.planLimit",
    text: "Your plan's guest list is full. Upgrade this event to add more people.",
  },
  [BULK_JOB_REASON.GAVE_UP]: { key: "events:guestImport.skip.gaveUp", text: "Could not be added after several tries. Add them again on their own." },
};

/** Who a refused row was, as it was pasted (a guest import's payload is an `ImportGuestRecipient`). */
const rowLabel = (row: BulkJobSkippedRow): string | null => {
  const r = (row.payload ?? {}) as ImportGuestRecipient;
  const name = [r.firstName, r.lastName].filter(Boolean).join(" ") || r.groupName || "";
  const contact = [r.email, r.phone ? formatPhone(r.phone) : null].filter(Boolean).join(" · ");

  return [name, contact].filter(Boolean).join(" — ") || null;
};

function GuestImportCard({ job, imports }: { job: BulkJob; imports: UseBulkJobsReturn }) {
  const { t } = useTranslation(["events"]);
  const [skippedRows, setSkippedRows] = useState<BulkJobSkippedRow[] | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  const [loadingSkipped, setLoadingSkipped] = useState(false);

  const total = job.totalRows ?? 0;
  const processed = job.processedRows ?? 0;
  const skipped = bulkJobCount(job, GUEST_IMPORT_ROW_OUTCOME.SKIP);
  const created = bulkJobCount(job, GUEST_IMPORT_ROW_OUTCOME.CREATE);
  const matched = bulkJobCount(job, GUEST_IMPORT_ROW_OUTCOME.MATCH);
  const attached = bulkJobCount(job, GUEST_IMPORT_ROW_OUTCOME.ATTACH);
  const open = bulkJobIsOpen(job.jobState);
  const failed = job.jobState === BULK_JOB_STATE.FAILED;

  const toggleSkipped = async () => {
    if (showSkipped) {
      setShowSkipped(false);

      return;
    }

    setShowSkipped(true);

    if (skippedRows) {
      return;
    }

    setLoadingSkipped(true);
    const res = await BulkJobService.report(job.id);
    setLoadingSkipped(false);

    if (res.code !== OP_RESULT_CODES.OK) {
      setShowSkipped(false);
      notifications.show({
        color: "red",
        title: t("events:guestImport.skippedFailed", { defaultValue: "Could not load the refused rows" }),
        message: refusalReason(res) || t("events:guestImport.tryAgain", { defaultValue: "Please try again." }),
      });

      return;
    }

    const full = (res.getDataFirst?.() ?? (res as any).data?.[0]) as BulkJob | undefined;
    setSkippedRows(full?.skippedRows ?? []);
  };

  const close = async () => {
    const res = await imports.dismiss(job.id);

    if (res.code !== OP_RESULT_CODES.OK) {
      notifications.show({
        color: "red",
        title: t("events:guestImport.closeFailed", { defaultValue: "Could not close the report" }),
        message: refusalReason(res) || t("events:guestImport.tryAgain", { defaultValue: "Please try again." }),
      });
    }
  };

  if (open) {
    return (
      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <Loader size="sm" mt={4} />
            <Text size="md" fw={600}>
              {t("events:guestImport.adding", {
                defaultValue: "Adding guests — {{processed}} of {{total}}",
                processed,
                total,
              })}
            </Text>
          </Group>
          <Progress value={total > 0 ? (processed / total) * 100 : 0} size="md" radius="xl" aria-label="Import progress" />
          <Text size="md" c="dimmed">
            {t("events:guestImport.carriesOn", {
              defaultValue: "This carries on if you leave this page — come back any time to see how it went.",
            })}
          </Text>
          {job.lastError ? (
            <Text size="md" c="orange.8">
              {t("events:guestImport.retrying", { defaultValue: "One guest could not be added yet and is being tried again." })}
            </Text>
          ) : null}
        </Stack>
      </Paper>
    );
  }

  const parts = [
    t("events:guestImport.added", { defaultValue: "{{count}} added", count: created }),
    matched ? t("events:guestImport.matched", { defaultValue: "{{count}} already on the guest list", count: matched }) : null,
    attached ? t("events:guestImport.attached", { defaultValue: "{{count}} joined their family's invitation", count: attached }) : null,
    skipped ? t("events:guestImport.skipped", { defaultValue: "{{count}} refused", count: skipped }) : null,
  ].filter(Boolean);

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
          <Box style={{ minWidth: 0 }}>
            <Text size="md" fw={600}>
              {failed
                ? t("events:guestImport.stopped", { defaultValue: "The guest import stopped" })
                : t("events:guestImport.finished", { defaultValue: "Guests added" })}
            </Text>
            <Text size="md" c="dimmed">
              {failed && job.lastError === BULK_JOB_REASON.SCOPE_GONE
                ? t("events:guestImport.eventGone", {
                    defaultValue: "The event was removed, so the rest of the list was not added. {{processed}} of {{total}} were dealt with.",
                    processed,
                    total,
                  })
                : failed
                  ? t("events:guestImport.stoppedEarly", {
                      defaultValue: "It stopped before the end: {{processed}} of {{total}} were dealt with. {{summary}}.",
                      processed,
                      total,
                      summary: parts.join(", "),
                    })
                  : `${parts.join(", ")}.`}
            </Text>
          </Box>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={() => void close()}
            loading={imports.dismissingId === job.id}
            aria-label={t("events:guestImport.close", { defaultValue: "Close this report" })}
          >
            <AppIcon iconName="X" size={18} />
          </ActionIcon>
        </Group>

        {skipped > 0 ? (
          <Box>
            <Button variant="subtle" size="md" px={0} onClick={() => void toggleSkipped()} loading={loadingSkipped}>
              {showSkipped
                ? t("events:guestImport.hideSkipped", { defaultValue: "Hide the refused rows" })
                : t("events:guestImport.showSkipped", { defaultValue: "Show the {{count}} refused", count: skipped })}
            </Button>
          </Box>
        ) : null}

        {showSkipped && skippedRows ? (
          <Stack gap="xs">
            {skippedRows.map((row) => {
              const reason = SKIP_REASON_TEXT[row.reason ?? ""];

              return (
                <Box key={row.rowIndex ?? rowLabel(row) ?? ""}>
                  <Text size="md">
                    {rowLabel(row) ??
                      ((row.payload as ImportGuestRecipient | null)?.personId
                        ? t("events:guestImport.aContact", { defaultValue: "A contact from your address book" })
                        : t("events:guestImport.row", { defaultValue: "Row {{n}}", n: (row.rowIndex ?? 0) + 1 }))}
                  </Text>
                  <Text size="md" c="dimmed">
                    {reason
                      ? t(reason.key, { defaultValue: reason.text })
                      : t("events:guestImport.skip.other", { defaultValue: "Not added." })}
                  </Text>
                </Box>
              );
            })}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** The event's imports still running, and the finished ones whose report is still open. Nothing when there are none. */
export function GuestImportStatus({ imports }: { imports: UseBulkJobsReturn }) {
  if (imports.jobs.length === 0) {
    return null;
  }

  return (
    <Stack gap="sm">
      {imports.jobs.map((job) => (
        <GuestImportCard key={job.id} job={job} imports={imports} />
      ))}
    </Stack>
  );
}
